/**
 * IATA airport code → city, state/country lookup.
 *
 * Hand-maintained list of the 130 highest-traffic airports the user is
 * likely to encounter. Falls back to an empty string when unknown so the
 * UI just hides the second line without showing "Unknown" garbage.
 */
const AIRPORT_CITIES = {
  // ===== USA (alphabetical by code) =====
  ABQ: 'Albuquerque, NM',
  ANC: 'Anchorage, AK',
  ATL: 'Atlanta, GA',
  AUS: 'Austin, TX',
  BDL: 'Hartford, CT',
  BHM: 'Birmingham, AL',
  BNA: 'Nashville, TN',
  BOI: 'Boise, ID',
  BOS: 'Boston, MA',
  BUF: 'Buffalo, NY',
  BUR: 'Burbank, CA',
  BWI: 'Baltimore, MD',
  BZN: 'Bozeman, MT',
  CHS: 'Charleston, SC',
  CLE: 'Cleveland, OH',
  CLT: 'Charlotte, NC',
  CMH: 'Columbus, OH',
  CVG: 'Cincinnati, OH',
  DAL: 'Dallas (Love), TX',
  DAY: 'Dayton, OH',
  DCA: 'Washington, DC',
  DEN: 'Denver, CO',
  DFW: 'Dallas, TX',
  DSM: 'Des Moines, IA',
  DTW: 'Detroit, MI',
  ELP: 'El Paso, TX',
  EUG: 'Eugene, OR',
  EWR: 'Newark, NJ',
  FAT: 'Fresno, CA',
  FLL: 'Fort Lauderdale, FL',
  GEG: 'Spokane, WA',
  GRR: 'Grand Rapids, MI',
  GSO: 'Greensboro, NC',
  GSP: 'Greenville, SC',
  HNL: 'Honolulu, HI',
  HOU: 'Houston (Hobby), TX',
  IAD: 'Washington Dulles, VA',
  IAH: 'Houston, TX',
  ICT: 'Wichita, KS',
  IND: 'Indianapolis, IN',
  JAN: 'Jackson, MS',
  JAX: 'Jacksonville, FL',
  JFK: 'New York, NY',
  KOA: 'Kona, HI',
  LAS: 'Las Vegas, NV',
  LAX: 'Los Angeles, CA',
  LGA: 'New York (LGA), NY',
  LGB: 'Long Beach, CA',
  LIT: 'Little Rock, AR',
  LIH: 'Lihue, HI',
  MCI: 'Kansas City, MO',
  MCO: 'Orlando, FL',
  MDW: 'Chicago Midway, IL',
  MEM: 'Memphis, TN',
  MIA: 'Miami, FL',
  MKE: 'Milwaukee, WI',
  MSP: 'Minneapolis, MN',
  MSY: 'New Orleans, LA',
  MYR: 'Myrtle Beach, SC',
  OAK: 'Oakland, CA',
  OGG: 'Maui, HI',
  OKC: 'Oklahoma City, OK',
  OMA: 'Omaha, NE',
  ONT: 'Ontario, CA',
  ORD: 'Chicago, IL',
  ORF: 'Norfolk, VA',
  PBI: 'West Palm Beach, FL',
  PDX: 'Portland, OR',
  PHL: 'Philadelphia, PA',
  PHX: 'Phoenix, AZ',
  PIT: 'Pittsburgh, PA',
  PNS: 'Pensacola, FL',
  PSP: 'Palm Springs, CA',
  PVD: 'Providence, RI',
  RDU: 'Raleigh-Durham, NC',
  RIC: 'Richmond, VA',
  RNO: 'Reno, NV',
  ROC: 'Rochester, NY',
  RSW: 'Fort Myers, FL',
  SAN: 'San Diego, CA',
  SAT: 'San Antonio, TX',
  SAV: 'Savannah, GA',
  SBA: 'Santa Barbara, CA',
  SDF: 'Louisville, KY',
  SEA: 'Seattle, WA',
  SFO: 'San Francisco, CA',
  SJC: 'San Jose, CA',
  SJU: 'San Juan, PR',
  SLC: 'Salt Lake City, UT',
  SMF: 'Sacramento, CA',
  SNA: 'Orange County, CA',
  STL: 'St. Louis, MO',
  SYR: 'Syracuse, NY',
  TPA: 'Tampa, FL',
  TUL: 'Tulsa, OK',
  TUS: 'Tucson, AZ',
  TYS: 'Knoxville, TN',
  VPS: 'Destin, FL',
  XNA: 'Bentonville, AR',
  // ===== Canada =====
  YEG: 'Edmonton, AB',
  YHZ: 'Halifax, NS',
  YOW: 'Ottawa, ON',
  YUL: 'Montreal, QC',
  YVR: 'Vancouver, BC',
  YWG: 'Winnipeg, MB',
  YYC: 'Calgary, AB',
  YYZ: 'Toronto, ON',
  // ===== Mexico / Latin America =====
  CUN: 'Cancun, MX',
  GDL: 'Guadalajara, MX',
  MEX: 'Mexico City, MX',
  PVR: 'Puerto Vallarta, MX',
  SJD: 'Los Cabos, MX',
  BOG: 'Bogota, CO',
  EZE: 'Buenos Aires, AR',
  GIG: 'Rio de Janeiro, BR',
  GRU: 'Sao Paulo, BR',
  SCL: 'Santiago, CL',
  LIM: 'Lima, PE',
  PTY: 'Panama City, PA',
  SDQ: 'Santo Domingo, DO',
  NAS: 'Nassau, BS',
  // ===== Europe =====
  AMS: 'Amsterdam, NL',
  ARN: 'Stockholm, SE',
  ATH: 'Athens, GR',
  BCN: 'Barcelona, ES',
  BER: 'Berlin, DE',
  BRU: 'Brussels, BE',
  CDG: 'Paris, FR',
  CPH: 'Copenhagen, DK',
  DUB: 'Dublin, IE',
  EDI: 'Edinburgh, UK',
  FCO: 'Rome, IT',
  FRA: 'Frankfurt, DE',
  GVA: 'Geneva, CH',
  HEL: 'Helsinki, FI',
  IST: 'Istanbul, TR',
  LGW: 'London Gatwick, UK',
  LHR: 'London, UK',
  LIN: 'Milan Linate, IT',
  LIS: 'Lisbon, PT',
  MAD: 'Madrid, ES',
  MUC: 'Munich, DE',
  MXP: 'Milan, IT',
  ORY: 'Paris Orly, FR',
  OSL: 'Oslo, NO',
  PRG: 'Prague, CZ',
  VCE: 'Venice, IT',
  VIE: 'Vienna, AT',
  WAW: 'Warsaw, PL',
  ZRH: 'Zurich, CH',
  // ===== Middle East / Asia / Oceania =====
  AUH: 'Abu Dhabi, AE',
  BKK: 'Bangkok, TH',
  BOM: 'Mumbai, IN',
  DEL: 'Delhi, IN',
  DOH: 'Doha, QA',
  DXB: 'Dubai, AE',
  HKG: 'Hong Kong',
  HND: 'Tokyo Haneda, JP',
  ICN: 'Seoul, KR',
  KIX: 'Osaka, JP',
  KUL: 'Kuala Lumpur, MY',
  MEL: 'Melbourne, AU',
  NRT: 'Tokyo Narita, JP',
  PEK: 'Beijing, CN',
  PVG: 'Shanghai, CN',
  SIN: 'Singapore',
  SYD: 'Sydney, AU',
  TLV: 'Tel Aviv, IL',
};

/** Returns "City, ST" for a 3-letter IATA code, or '' if unknown. */
export const getAirportCity = (code) => {
  if (!code) return '';
  return AIRPORT_CITIES[code.toString().toUpperCase().trim()] || '';
};

export default AIRPORT_CITIES;
